import { t } from '@lingui/macro';
import { isEqual, intersection } from 'lodash';
import { Laptop } from '@material-ui/icons';

import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    AGENT_NAME_FIELD,
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_LOCAL_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TEST_COUNT_DISPLAY_FIELD,
    AGENT_TAGS_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TEST_COUNT_FIELD,
    AGENT_NETWORK_IPV6_LOCAL_FIELD,
    AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
} from '../../pages-admin/agents/AgentConstants';
import { renderStatus, renderTestCount } from '../../pages-admin/agents/AgentRenderHelper';
import { NPoint2K, NPoint3K, NPoint4K, NPoint4KW } from '../../icon';
import { formatTags } from '../../../utilities/StringUtilities';
import { testAgentsParser } from './TestAgentsParserFilter';
import { AGENTS_CONTAINER_CLASS, MORE_AGENTS_ICON_CLASS } from './WebTestNowStyles';
import { SEM_FIELD } from '../../pages-admin/service-tests/ServiceTestAdminConstants';

/**
 * Gets the correct agent icon using API agent data
 * We have to do this rather than using the LegacyIcon component because this page uses V3 to get agent
 * data, which does not return icon data.
 * @param {Object} agentData
 * @returns agent icon
 */
function getAgentIconFromGeneration(agentData) {
    switch (agentData.generation) {
        case 2:
            return <NPoint2K />;
        case 3:
            return <NPoint3K />;
        case 4:
            return agentData.hasWireless ? <NPoint4KW /> : <NPoint4K />;
        default:
            return <Laptop />;
    }
}

/**
 * Column builder for agent selector and matched tag agents data tables
 * @returns array of data table columns for agents
 */
export function buildWebTestNowAgentPickerGridColumns() {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 80,
            mapping: 'agent.generation',
            icons: [
                {
                    getIcon: (row) => getAgentIconFromGeneration(row),
                    getTooltip: (row) => row[AGENT_TRANSLATED_TYPE_FIELD],
                },
            ],
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 84,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Service Tests`,
            sortable: true,
            sortProperty: AGENT_TEST_COUNT_FIELD,
            mapping: AGENT_TEST_COUNT_DISPLAY_FIELD,
            renderer: renderTestCount,
            width: 105,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SEM_FIELD,
            title: t`Smart Edge Monitoring`,
            align: 'center',
            sortable: true,
            formatter: (value) => {
                return value === true ? t`On` : t`Off`;
            },
            width: 175,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: AGENT_TAGS_FIELD,
            title: t`Tags`,
            sortable: false,
            formatter: formatTags,
            width: 190,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IPv6`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV6_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
            width: 280,
        },
    ];
}

/**
 * Returns a list of agents filtered by the selected tags and tag logic
 * @param {object[]} agentList
 * @param {string[]} selectedTags
 * @param {'all' | 'any'} tagLogic
 * @returns
 */
export function filterAgentsBySelectedTags(agentList, selectedTags, tagLogic) {
    return tagLogic === 'all'
        ? agentList
              .filter((agentData) => agentData.tags.length > 0)
              .filter((agentData) => {
                  const commonTags = intersection(agentData.tags, selectedTags);
                  return selectedTags.length > 0 && isEqual(selectedTags, commonTags);
              })
        : agentList.filter((agentData) => selectedTags.some((submittedTag) => agentData.tags.includes(submittedTag)));
}

export function isWirelessAgent(agent) {
    return agent.generation > 2 && agent.hasWireless && agent.isWirelessEnabled === true;
}

export function renderAgents(row) {
    const result = testAgentsParser(row.agents, true, 1);

    return (
        <div className={AGENTS_CONTAINER_CLASS}>
            {result.agentText}
            {result.agentHelpText && (
                <div className={MORE_AGENTS_ICON_CLASS} title={result.agentHelpText}>
                    <HelpOutlineIcon />
                </div>
            )}
        </div>
    );
}
